import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface CarrierCharge {
  id?: number;
  carrierId: number;
  carrierName?: string;
  chargeTypeId: number;
  chargeTypeName?: string;
  shipmentMode: 'FCL' | 'LCL' | 'AIR' | 'RORO';
  rate: number;
  currency: 'USD' | 'PHP';
  originPortId?: number;
  originPortName?: string;
  destinationPortId?: number;
  destinationPortName?: string;
  validFrom: Date;
  validUntil?: Date;
  notes?: string;
  isActive?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

@Injectable({
  providedIn: 'root'
})
export class CarrierChargesService {
  private apiUrl = `${environment.apiUrl}/forwarding/carrier-charges`;
  private charges$ = new BehaviorSubject<CarrierCharge[]>([]);

  constructor(private http: HttpClient) {
    this.loadCharges();
  }

  private loadCharges(): void {
    console.log('[CarrierChargesService] Loading carrier charges from:', this.apiUrl);
    this.http.get<CarrierCharge[]>(this.apiUrl).subscribe({
      next: (charges) => {
        console.log('[CarrierChargesService] Successfully loaded carrier charges from API:', charges.length);
        // Convert date strings to Date objects
        const processedCharges = charges.map(c => ({
          ...c,
          validFrom: new Date(c.validFrom),
          validUntil: c.validUntil ? new Date(c.validUntil) : undefined,
          createdAt: c.createdAt ? new Date(c.createdAt) : undefined,
          updatedAt: c.updatedAt ? new Date(c.updatedAt) : undefined
        }));
        this.charges$.next(processedCharges);
      },
      error: (error) => {
        console.error('[CarrierChargesService] Error loading carrier charges from API, falling back to empty array:', error);
        this.charges$.next([]);
      }
    });
  }

  getCharges(): Observable<CarrierCharge[]> {
    return this.charges$.asObservable();
  }

  getChargesList(): CarrierCharge[] {
    return this.charges$.value;
  }

  getActiveCharges(): Observable<CarrierCharge[]> {
    return this.http.get<CarrierCharge[]>(`${this.apiUrl}/active`);
  }

  getByCarrier(carrierId: number): Observable<CarrierCharge[]> {
    return this.http.get<CarrierCharge[]>(`${this.apiUrl}/carrier/${carrierId}`);
  }

  getByChargeType(chargeTypeId: number): Observable<CarrierCharge[]> {
    return this.http.get<CarrierCharge[]>(`${this.apiUrl}/charge-type/${chargeTypeId}`);
  }

  getByMode(shipmentMode: string): Observable<CarrierCharge[]> {
    return this.http.get<CarrierCharge[]>(`${this.apiUrl}/mode/${shipmentMode}`);
  }

  getByRoute(originPortId?: number, destinationPortId?: number): Observable<CarrierCharge[]> {
    let url = `${this.apiUrl}/route?`;
    if (originPortId) url += `originPortId=${originPortId}&`;
    if (destinationPortId) url += `destinationPortId=${destinationPortId}`;
    return this.http.get<CarrierCharge[]>(url);
  }

  addCharge(charge: Omit<CarrierCharge, 'id' | 'createdAt' | 'carrierName' | 'chargeTypeName' | 'originPortName' | 'destinationPortName'>): void {
    this.http.post<CarrierCharge>(this.apiUrl, charge).subscribe({
      next: () => {
        console.log('[CarrierChargesService] Carrier charge created, reloading');
        this.loadCharges();
      },
      error: (error) => console.error('[CarrierChargesService] Error adding carrier charge:', error)
    });
  }

  updateCharge(id: number, charge: Partial<CarrierCharge>): void {
    this.http.put<CarrierCharge>(`${this.apiUrl}/${id}`, charge).subscribe({
      next: () => {
        console.log('[CarrierChargesService] Carrier charge updated, reloading');
        this.loadCharges();
      },
      error: (error) => console.error('[CarrierChargesService] Error updating carrier charge:', error)
    });
  }

  deleteCharge(id: number): void {
    this.http.delete(`${this.apiUrl}/${id}`).subscribe({
      next: () => {
        console.log('[CarrierChargesService] Carrier charge deleted, reloading');
        this.loadCharges();
      },
      error: (error) => console.error('[CarrierChargesService] Error deleting carrier charge:', error)
    });
  }
}
